import { useQuery } from '@tanstack/react-query'
import { STAGES } from '@joker/domain'
import type { Program } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, fmtDate } from '../ui'
import { Training } from './Person'

/**
 * Programs: one run of training on one account. A person can have several — a
 * second programme, a second year, a changed condition — and each keeps its
 * own sessions, so two runs can be compared side by side.
 */

function title(g: Program): string {
  return g.programClass ? `${g.programClass}` : (g.accountName ?? 'Program')
}

function progress(g: Program): string | null {
  if (!g.programSessions) return null
  return `${Math.min(100, Math.round((g.sessionsTotal / g.programSessions) * 100))}%`
}

/** A person's programs, on their page. Hidden when they have never had an account. */
export function Programs({ personId, onOpenProgram }: { personId: string; onOpenProgram?: (id: string) => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.programs.forPerson.queryOptions({ personId }))
  if (!q.data || q.data.length === 0) return null
  return (
    <Panel title="Programs" note={`${q.data.length} — one per training account`}>
      <table className="data programs">
        <thead>
          <tr>
            <th>Program</th>
            <th>Account</th>
            <th className="r">Sessions</th>
            <th className="r">Progress</th>
            <th>First session</th>
            <th>Last session</th>
            <th>Stage</th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((g) => (
            <tr
              key={g.id}
              {...(onOpenProgram
                ? { tabIndex: 0, onClick: () => onOpenProgram(g.id), onKeyDown: (e) => e.key === 'Enter' && onOpenProgram(g.id) }
                : {})}
            >
              <td>
                <strong>{title(g)}</strong>
                {g.accountName && g.programClass && (
                  <>
                    <br />
                    <span className="muted sub-line">{g.accountName}</span>
                  </>
                )}
              </td>
              <td className="num">
                {g.oryxUserNumber != null ? `#${g.oryxUserNumber}` : <span className="muted">—</span>}
                <br />
                <span className="muted sub-line">{g.source === 'cortex' ? 'Cortex' : 'Oryx app'}</span>
              </td>
              <td className="r num">
                {g.sessionsTotal}
                {g.programSessions ? <span className="muted"> / {g.programSessions}</span> : null}
              </td>
              <td className="r num">{progress(g) ?? <span className="muted">—</span>}</td>
              <td className="num">{g.firstSessionAt ? fmtDate(g.firstSessionAt) : <span className="muted">—</span>}</td>
              <td className="num">{g.lastSessionAt ? fmtDate(g.lastSessionAt) : <span className="muted">—</span>}</td>
              <td>{STAGES.find((s) => s.id === g.stage)?.label ?? g.stage}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  )
}

/** One program, at /programs/:id: whose it is, how far it got, and every session. */
export function ProgramPage({
  id,
  onBack,
  onOpenPerson,
}: {
  id: string
  onBack: () => void
  onOpenPerson: (id: string) => void
}) {
  const trpc = useTRPC()
  const q = useQuery(trpc.programs.byId.queryOptions({ id }))
  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="the program" />
  const g = q.data
  return (
    <>
      <button className="back" onClick={onBack}>
        ← Back
      </button>
      <h2 className="view-title">{title(g)}</h2>
      <p className="view-sub">
        <button type="button" className="linklike" onClick={() => onOpenPerson(g.personId)}>
          {g.personName ?? 'Open the person'}
        </button>
        {g.oryxUserNumber != null && <> · account #{g.oryxUserNumber}</>} ·{' '}
        {g.source === 'cortex' ? 'Cortex' : 'Oryx app'}
        {g.accountName && <> · named “{g.accountName}”</>}
      </p>

      <div className="tiles">
        <Tile k="Sessions" v={g.sessionsTotal} d={g.programSessions ? `of ${g.programSessions} in the programme` : 'no programme length set'} />
        <Tile k="Progress" v={progress(g) ?? '—'} d="of the programme" />
        <Tile k="First session" v={g.firstSessionAt ? fmtDate(g.firstSessionAt) : '—'} />
        <Tile k="Last session" v={g.lastSessionAt ? fmtDate(g.lastSessionAt) : '—'} d={STAGES.find((s) => s.id === g.stage)?.label ?? g.stage} />
      </div>

      <Training id={g.id} />
    </>
  )
}
